import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Inbox, Save, Video, LogOut, Upload, Link as LinkIcon, X, Loader2, User, CheckCircle2, Clock, Archive } from "lucide-react";
import { toast, Toaster } from "sonner";
import BrandHeader from "../components/BrandHeader";
import DashboardTracker from "../components/tracker/DashboardTracker";
import CoachesHelper from "../components/tracker/CoachesHelper";
import CoachLogoutButton from "../components/CoachLogoutButton";
import {
  listCoachClients,
  getVideoSetting,
  setVideoSetting,
  uploadVideoFile,
  clearVideoSetting,
} from "../lib/api";
import { sydDate, sydTime } from "../lib/time";

const formatDate = (iso) => sydDate(iso, "None");
const formatTime = (iso) => sydTime(iso);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Coach() {
  const [items, setItems] = useState(null);
  // Distinguishes an authentication failure from any other network/server
  // failure so we never render "no clients yet" when the list request fails.
  //   null            -> no error / not yet loaded
  //   { kind: 'auth' } -> 401 or 403 from the coach list endpoint
  //   { kind: 'server', message }
  const [listError, setListError] = useState(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [videoSource, setVideoSource] = useState("none");
  const [videoFilePath, setVideoFilePath] = useState("");
  const [savingVideo, setSavingVideo] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Active Clients is the default view; ?view=inactive opens former clients.
  const view = searchParams.get("view") === "inactive" ? "inactive" : "active";
  const setView = (v) => setSearchParams(v === "inactive" ? { view: "inactive" } : {}, { replace: true });

  const refreshClients = () => {
    setListError(null);
    return listCoachClients()
      .then((rows) => {
        setItems(Array.isArray(rows) ? rows : []);
        setListError(null);
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 401 || status === 403) setListError({ kind: "auth" });
        else setListError({ kind: "server", message: err?.message || "Couldn't load your client accounts." });
        setItems((prev) => (Array.isArray(prev) ? prev : []));
      });
  };

  const refreshVideo = () => {
    getVideoSetting()
      .then((v) => {
        setVideoUrl(v?.url || "");
        setVideoFilePath(v?.upload_path || "");
        setVideoSource(v?.source || "none");
      })
      .catch(() => {});
  };

  useEffect(() => {
    refreshClients();
    refreshVideo();
    const onFocus = () => refreshClients();
    const onVisible = () => { if (document.visibilityState === "visible") refreshClients(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveVideoUrl = async () => {
    const trimmed = videoUrl.trim();
    if (!trimmed) return;
    setSavingVideo(true);
    try {
      await setVideoSetting(trimmed);
      toast.success("Welcome video URL saved");
      refreshVideo();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save video URL");
    } finally {
      setSavingVideo(false);
    }
  };

  const onPickFile = () => { if (!uploadingVideo) fileInputRef.current?.click(); };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("video/")) { toast.error("Please choose a video file."); return; }
    if (file.size > 200 * 1024 * 1024) { toast.error("Video is too large (max 200MB)."); return; }
    setUploadingVideo(true); setUploadPct(0);
    try {
      await uploadVideoFile(file, (pct) => setUploadPct(pct));
      toast.success("Welcome video uploaded");
      refreshVideo();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Video upload failed");
    } finally {
      setUploadingVideo(false); setUploadPct(0);
    }
  };

  const clearVideo = async () => {
    try {
      await clearVideoSetting();
      toast.success("Welcome video cleared");
      refreshVideo();
    } catch { toast.error("Couldn't clear video"); }
  };

  const absoluteVideoSrc =
    videoSource === "upload" && videoFilePath
      ? `${BACKEND_URL}${videoFilePath}${videoFilePath.includes("?") ? "&" : "?"}v=${videoFilePath.length}`
      : "";

  const allItems = Array.isArray(items) ? items : [];
  const activeItems = allItems.filter((c) => c.status !== "inactive");
  const inactiveItems = allItems.filter((c) => c.status === "inactive");
  const [search, setSearch] = useState("");
  const needle = search.trim().toLowerCase();
  const matches = (c) => !needle || [c.full_name, c.preferred_name, c.email].some((v) => (v || "").toLowerCase().includes(needle));
  const visible = view === "inactive" ? inactiveItems : activeItems.filter(matches);

  return (
    <div className="min-h-screen bg-brand-base">
      <Toaster richColors position="top-center" />
      <BrandHeader small right={<CoachLogoutButton />} />

      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="label-eyebrow flex items-center gap-3">
              <span className="inline-block h-px w-10 bg-brand-primary" />
              Coach Dashboard
            </div>
            <h1 className="font-serif text-4xl md:text-5xl text-brand-ink mt-3">Coach Dashboard</h1>
          </div>
        </div>

        <DashboardTracker />

        <CoachesHelper />

        {/* Client Accounts list */}
        <div className="mt-12" data-testid="client-accounts-section">
          <div className="label-eyebrow flex items-center gap-3 mb-5">
            <User size={14} strokeWidth={1.8} />
            HER Aligned Client Accounts
          </div>
          <h2 className="font-serif text-3xl text-brand-ink -mt-2 mb-2">Client Accounts</h2>
          <p className="mb-6 text-brand-ink-muted text-base">Every HER Aligned client with an authenticated account. Click a client to open her Client Profile.</p>

          <div className="flex items-center gap-2 mb-6" role="tablist" data-testid="client-status-tabs">
            <button
              type="button"
              role="tab"
              aria-selected={view === "active"}
              onClick={() => setView("active")}
              className={`rounded-full px-5 py-2 text-xs uppercase tracking-[0.2em] font-bold border transition-colors ${
                view === "active" ? "bg-brand-ink text-white border-brand-ink" : "border-brand-border text-brand-ink-muted hover:text-brand-ink"
              }`}
              data-testid="clients-tab-active"
            >
              Active Clients <span className="ml-1 opacity-70" data-testid="clients-tab-active-count">({activeItems.length})</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === "inactive"}
              onClick={() => setView("inactive")}
              className={`rounded-full px-5 py-2 text-xs uppercase tracking-[0.2em] font-bold border transition-colors inline-flex items-center gap-2 ${
                view === "inactive" ? "bg-brand-ink text-white border-brand-ink" : "border-brand-border text-brand-ink-muted hover:text-brand-ink"
              }`}
              data-testid="clients-tab-inactive"
            >
              <Archive size={12} strokeWidth={2} /> Inactive Clients <span className="ml-1 opacity-70" data-testid="clients-tab-inactive-count">({inactiveItems.length})</span>
            </button>
          </div>

          {view === "active" && (
            <input type="search" className="input-warm w-full mb-5" placeholder="Search Active Clients by name or email" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="active-clients-search" />
          )}

          {items === null && !listError ? (
            <p className="text-brand-ink-muted" data-testid="admin-loading">Loading…</p>
          ) : listError?.kind === "auth" ? (
            <div className="surface-card p-12 text-center" data-testid="admin-auth-error">
              <LogOut className="mx-auto text-brand-primary" strokeWidth={1.4} size={32} />
              <p className="mt-4 font-serif text-2xl text-brand-ink">Your coach session has expired.</p>
              <p className="mt-2 text-brand-ink-muted">Please sign back in to see your client accounts.</p>
              <button type="button" onClick={() => navigate("/coach-login", { replace: true })} className="btn-primary inline-flex items-center gap-2 mt-6" data-testid="admin-signin-again-btn">
                <LogOut size={16} strokeWidth={1.8} /> Sign in again
              </button>
            </div>
          ) : listError?.kind === "server" ? (
            <div className="surface-card p-12 text-center" data-testid="admin-server-error">
              <Inbox className="mx-auto text-red-700" strokeWidth={1.4} size={32} />
              <p className="mt-4 font-serif text-2xl text-brand-ink">Couldn&apos;t load your client accounts.</p>
              <p className="mt-2 text-sm text-brand-ink-muted break-words">{listError.message}</p>
              <button type="button" onClick={() => refreshClients()} className="btn-primary inline-flex items-center gap-2 mt-6" data-testid="admin-retry-btn">
                <Loader2 size={16} strokeWidth={1.8} /> Try again
              </button>
            </div>
          ) : view === "active" && needle && visible.length === 0 ? (
            <p className="surface-card p-8 text-brand-ink-muted" data-testid="active-clients-search-empty">No Active Clients match “{search.trim()}”.</p>
          ) : visible.length === 0 ? (
            <div className="surface-card p-12 text-center" data-testid={view === "inactive" ? "admin-empty-inactive" : "admin-empty"}>
              <Inbox className="mx-auto text-brand-primary" strokeWidth={1.4} size={32} />
              {view === "inactive" ? (
                <>
                  <p className="mt-4 font-serif text-2xl text-brand-ink">No inactive clients.</p>
                  <p className="mt-2 text-brand-ink-muted">Former clients you mark Inactive will be kept here for your records.</p>
                </>
              ) : (
                <>
                  <p className="mt-4 font-serif text-2xl text-brand-ink">No active client accounts yet.</p>
                  <p className="mt-2 text-brand-ink-muted">The first client who signs up will appear here.</p>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-3" data-testid={view === "inactive" ? "admin-list-inactive" : "admin-list"}>
              {visible.map((c) => (
                <Link
                  key={c.id}
                  to={`/coach/clients/${c.id}`}
                  className={`surface-card p-5 md:p-6 flex items-center justify-between gap-4 hover:shadow-md transition-shadow ${c.status === "inactive" ? "opacity-80" : ""}`}
                  data-testid={`admin-client-${c.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-2xl text-brand-ink truncate" data-testid={`admin-client-name-${c.id}`}>
                      {c.full_name || c.email}
                      {c.preferred_name && c.preferred_name !== c.full_name && (
                        <span className="ml-2 text-brand-ink-muted text-lg font-normal">· prefers {c.preferred_name}</span>
                      )}
                    </div>
                    <div className="text-sm text-brand-ink-muted mt-1 truncate" data-testid={`admin-client-email-${c.id}`}>{c.email}</div>
                    <div className="text-xs text-brand-ink-muted mt-2 flex items-center gap-3 flex-wrap">
                      <span>Signed up {formatDate(c.created_at)}</span>
                      <span data-testid={`admin-client-week-${c.id}`}>Week {c.tenure_week || 1}</span>
                      {c.status === "inactive" && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-brand-ink/5 px-2 py-0.5 text-brand-ink" data-testid={`admin-client-inactive-${c.id}`}>Inactive</span>
                      )}
                      {c.roadmap_completed ? (
                        <span className="inline-flex items-center gap-1 text-emerald-800" data-testid={`admin-client-roadmap-done-${c.id}`}>
                          <CheckCircle2 size={12} strokeWidth={2} /> Roadmap completed {formatDate(c.latest_roadmap_created_at)}
                          {!c.latest_roadmap_has_pdf && <span className="ml-1 text-amber-700">· PDF generating…</span>}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-800" data-testid={`admin-client-roadmap-pending-${c.id}`}>
                          <Clock size={12} strokeWidth={2} /> Roadmap not completed
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0 text-xs uppercase tracking-[0.2em] font-bold text-brand-ink-muted">
                    Open profile →
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Welcome video (unchanged Stage 1 tooling) */}
        <div className="surface-card mt-12 p-6 md:p-8" data-testid="welcome-video-section">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="label-eyebrow flex items-center gap-3">
                <Video size={14} strokeWidth={1.8} />
                Client welcome video
              </div>
              <h2 className="font-serif text-2xl text-brand-ink mt-2">Video shown on the client Results page</h2>
              <p className="mt-2 text-sm text-brand-ink-muted">
                Upload a file or paste a public URL. The most recently saved source is what plays.
              </p>
            </div>
            {videoSource !== "none" && (
              <button
                type="button"
                onClick={clearVideo}
                className="btn-ghost border border-brand-border rounded-full px-4 py-2 text-xs uppercase tracking-[0.2em] font-bold text-brand-ink-muted hover:text-red-700"
                data-testid="video-clear-btn"
              >
                <X size={14} strokeWidth={1.8} /> Clear current video
              </button>
            )}
          </div>

          {videoSource === "upload" && absoluteVideoSrc && (
            <div className="mt-4 rounded-2xl overflow-hidden border border-brand-border bg-black" data-testid="video-current-upload">
              <video src={absoluteVideoSrc} className="w-full aspect-video object-cover" controls muted />
            </div>
          )}
          {videoSource === "url" && videoUrl && (
            <p className="mt-3 text-sm text-brand-ink-muted break-all" data-testid="video-current-url">
              Currently using URL: <span className="text-brand-ink">{videoUrl}</span>
            </p>
          )}

          <div className="mt-6">
            <div className="flex items-center gap-2 text-sm font-semibold text-brand-ink">
              <Upload size={14} strokeWidth={1.8} /> Upload from your device
            </div>
            <p className="mt-1 text-xs text-brand-ink-muted">MP4, MOV or WebM. Max 200MB.</p>
            <input ref={fileInputRef} type="file" accept="video/*" className="hidden" onChange={onFileChange} data-testid="video-file-input" />
            <div className="mt-3 flex items-center gap-3">
              <button
                type="button"
                onClick={onPickFile}
                disabled={uploadingVideo}
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-70 disabled:cursor-wait"
                data-testid="video-upload-btn"
              >
                {uploadingVideo ? (<><Loader2 size={16} strokeWidth={1.8} className="animate-spin" /> Uploading… {uploadPct}%</>) : (<><Upload size={16} strokeWidth={1.8} /> Choose video file</>)}
              </button>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-brand-border">
            <div className="flex items-center gap-2 text-sm font-semibold text-brand-ink">
              <LinkIcon size={14} strokeWidth={1.8} /> Or paste a video URL
            </div>
            <p className="mt-1 text-xs text-brand-ink-muted">YouTube, Vimeo or direct video link. Saving a URL replaces any uploaded file.</p>
            <div className="mt-3 flex flex-col sm:flex-row gap-3">
              <input type="url" className="input-warm flex-1" placeholder="https://youtube.com/watch?v=…" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} data-testid="video-url-input" />
              <button onClick={saveVideoUrl} disabled={savingVideo || !videoUrl.trim()} className="btn-primary inline-flex items-center gap-2 disabled:opacity-70" data-testid="video-save-btn">
                <Save size={16} strokeWidth={1.8} /> {savingVideo ? "Saving…" : "Save URL"}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
